import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useNavigate } from "react-router-dom";

import { Text } from "@/atoms";
import { cn } from "@/lib/utils";
import {
  getReferralRewardCardDescription,
  getReferralRewardCardTitle,
} from "@/modules/referralRewards";
import { get } from "api";
import { Button } from "atoms/button/Button";
import RewardIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import IncreasingArrowRightIcon from "atoms/icon/icons/increasing_arrow_right.svg";
import { hexToRgb } from "toolbox/Colors";

import { useReferralRewardsFeature } from "../useReferralRewardsFeature/useReferralRewardsFeature";
import { useShareReferralReward } from "../useShareReferralReward/useShareReferralReward";

export const ReferralRewardsCard = () => {
  const {
    showReferralRewards,
    referralRewardForPlatformLocation,
    activeSignupReward,
  } = useReferralRewardsFeature();

  const navigate = useNavigate();

  const { data: platform } = useQuery({
    queryKey: ["/platform/current"],
    queryFn: () => get("/platform/current"),
  });

  const { data: theme } = useQuery({
    queryKey: ["/platform/theme"],
    queryFn: () => get("/platform/theme"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const handleTrackReferrals = () => {
    navigate("/rewards/referrals");
  };

  const { shareReferralReward } = useShareReferralReward({
    platform: platform!,
    rewardTitle: activeSignupReward?.title ?? "a reward",
    user: user!,
  });

  const brandColor = theme?.brandColor || "#000000";

  const handleShareClick = () => {
    if (referralRewardForPlatformLocation && activeSignupReward) {
      shareReferralReward();
    }
  };

  if (!showReferralRewards) {
    return null;
  }

  const renderActions = () => (
    <div
      className={cn(
        " flex flex-row gap-2",
        referralRewardForPlatformLocation && activeSignupReward
          ? "justify-center"
          : "justify-start"
      )}
    >
      <div className="">
        <Button
          style="secondary"
          iconSize="x3small"
          endIconSVG={IncreasingArrowRightIcon}
          size="xsmall"
          fullWidth
          onClick={handleTrackReferrals}
          data-testid="track-referrals-button"
        >
          <div className="flex justify-center">
            <span className="ml-1 mr-2 whitespace-nowrap text-nowrap text-sm font-semibold">
              Track Referrals
            </span>
          </div>
        </Button>
      </div>

      {referralRewardForPlatformLocation && activeSignupReward && (
        <div className="">
          <Button
            style="primary"
            iconSize="x3small"
            size="xsmall"
            fullWidth
            endIconSVG={RewardIcon}
            onClick={handleShareClick}
            data-testid="refer-friend-button"
          >
            <div className="flex justify-center">
              <span className="ml-1 mr-2 whitespace-nowrap text-nowrap text-sm font-semibold">
                Refer a Friend
              </span>
            </div>
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cx(
        "ReferralRewardsCard relative mt-4 overflow-hidden bg-[#FFFFFF] p-6 text-left shadow-[0px_2px_10px_0px_rgba(0,0,0,0.06)]",
        "mx-2 mb-4 rounded-xl"
      )}
    >
      <div
        className="pointer-events-none absolute"
        style={getGiftCardStyle(brandColor)}
      >
        <RewardIcon
          style={{
            width: "100%",
            height: "100%",
            color: brandColor,
            fill: brandColor,
          }}
        />
      </div>

      <div className="relative flex flex-col gap-6">
        <div className="flex flex-col">
          <Text
            variant="h2"
            className="mb-3 text-2xl font-semibold leading-tight text-gray-900"
            text={getReferralRewardCardTitle(referralRewardForPlatformLocation)}
          />
          <Text
            variant="body1"
            component="p"
            className="text-sm text-gray-600"
            text={getReferralRewardCardDescription(
              referralRewardForPlatformLocation,
              activeSignupReward
            )}
          />
        </div>

        {renderActions()}
      </div>
    </div>
  );
};

function getGiftCardStyle(brandColor: string) {
  const rgbBrandColor = hexToRgb(brandColor)?.trim().split(/\s+/).join(", ");

  return {
    width: "430px",
    height: "265px",
    right: "-200px",
    top: "30%",
    transform: "rotate(-20.445deg) translateY(-50%)",
    flexShrink: 0,
    opacity: 0.15,
    color: brandColor || "#000000",
    maskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.1) 10%, rgba(${rgbBrandColor},.9) 100%)`,
    WebkitMaskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.1) 10%, rgba(${rgbBrandColor},.9) 100%)`,
  };
}
